import { ApiError, apiRequest } from "./apiClient";
import { mapFieldsJsonToWizardFields } from "../utils/gameRuleFields";
import type { GameRuleTemplateDetail, GameRuleTemplateListRow, GameRuleWizardField } from "../types/gameRuleTemplate";

type CreateResponse = {
  readonly success: boolean;
  readonly data: {
    readonly game_rule_template_id: string;
    readonly stored_file_id: string;
    readonly document_web_url: string | null;
  };
};

type DetailResponse = {
  readonly success: boolean;
  readonly data: {
    readonly id: string;
    readonly name: string;
    readonly description: string | null;
    readonly status: string;
    readonly stored_file_id: string | null;
    readonly document_web_url: string | null;
    readonly fields_json: unknown[];
    readonly created_at: string;
    readonly updated_at: string;
    readonly created_by: string;
  };
};

type ListResponse = {
  readonly success: boolean;
  readonly data: Array<{
    readonly id: string;
    readonly name: string;
    readonly description?: string | null;
    readonly status: string;
    readonly stored_file_id: string | null;
    readonly document_web_url: string | null;
    readonly created_at: string;
    readonly updated_at: string;
    readonly created_by: string;
  }>;
};

export const createGameRuleTemplate = async (
  name: string,
  description: string | undefined,
  file: File
): Promise<{ templateId: string; storedFileId: string; documentWebUrl: string | null }> => {
  const fd = new FormData();
  fd.append("name", name.trim());
  if (description?.trim()) {
    fd.append("description", description.trim());
  }
  fd.append("file", file);
  const response = await apiRequest("/game-rule-templates", { method: "POST", body: fd });
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as CreateResponse;
  const d = json.data;
  return {
    templateId: d.game_rule_template_id,
    storedFileId: d.stored_file_id,
    documentWebUrl: d.document_web_url ?? null
  };
};

export const listGameRuleTemplates = async (): Promise<GameRuleTemplateListRow[]> => {
  const response = await apiRequest("/game-rule-templates", undefined);
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as ListResponse;
  if (!Array.isArray(json.data)) {
    return [];
  }
  return json.data.map((r) => ({
    id: r.id,
    name: r.name,
    description: r.description ?? null,
    status: r.status,
    storedFileId: r.stored_file_id,
    documentWebUrl: r.document_web_url,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
    createdBy: r.created_by
  }));
};

export const getGameRuleTemplate = async (templateId: string): Promise<GameRuleTemplateDetail> => {
  const response = await apiRequest(`/game-rule-templates/${templateId}`, undefined);
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as DetailResponse;
  const d = json.data;
  const fj = Array.isArray(d.fields_json) ? d.fields_json : [];
  const fieldsJson = fj.filter((x): x is Record<string, unknown> => x !== null && typeof x === "object" && !Array.isArray(x));
  return {
    id: String(d.id),
    name: d.name,
    description: d.description ?? null,
    status: d.status,
    storedFileId: d.stored_file_id != null ? String(d.stored_file_id) : null,
    documentWebUrl: d.document_web_url ?? null,
    fieldsJson,
    createdAt: d.created_at,
    updatedAt: d.updated_at,
    createdBy: String(d.created_by)
  };
};

/** Server fields_json rows → wizard fields (for edit UI). */
export const detailFieldsToWizardFields = (detail: GameRuleTemplateDetail): GameRuleWizardField[] =>
  mapFieldsJsonToWizardFields(detail.fieldsJson);

const fieldsToJsonPayload = (fields: GameRuleWizardField[]): Array<Record<string, unknown>> =>
  fields.map((f) => {
    const base: Record<string, unknown> = {
      id: f.id,
      key: f.key,
      label: f.label,
      type: f.type,
      required: f.required
    };
    if (f.type === "checkbox" || f.type === "radio" || f.type === "select") {
      base.options = f.options.map((o) => ({
        value: o.value,
        label: o.label.trim() ? o.label : null
      }));
    }
    return base;
  });

export type GameRuleTemplatePatchPayload = {
  readonly name?: string;
  readonly description?: string | null;
  readonly fields?: GameRuleWizardField[];
};

/** PATCH template metadata and/or field definitions (no document bytes). */
export const patchGameRuleTemplate = async (
  templateId: string,
  patch: GameRuleTemplatePatchPayload
): Promise<void> => {
  const payload: Record<string, unknown> = {};
  if (patch.name !== undefined) {
    payload.name = patch.name.trim();
  }
  if (patch.description !== undefined) {
    const d = patch.description;
    payload.description = d === null || (typeof d === "string" && !d.trim()) ? null : String(d).trim();
  }
  if (patch.fields !== undefined) {
    payload.fields_json = fieldsToJsonPayload(patch.fields);
  }
  if (Object.keys(payload).length === 0) {
    return;
  }
  const response = await apiRequest(`/game-rule-templates/${templateId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
};

export const patchGameRuleTemplateFields = async (
  templateId: string,
  fields: GameRuleWizardField[]
): Promise<void> => {
  await patchGameRuleTemplate(templateId, { fields });
};

export const putGameRuleTemplateDocument = async (
  templateId: string,
  blob: Blob
): Promise<void> => {
  const fd = new FormData();
  fd.append("file", blob, "document.docx");
  const response = await apiRequest(
    `/game-rule-templates/${templateId}/document`,
    { method: "PUT", body: fd }
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
};

async function parseDetail(response: Response): Promise<string> {
  const raw = await response.text();
  try {
    const o = JSON.parse(raw) as { detail?: string };
    return typeof o.detail === "string" ? o.detail : raw || `HTTP ${response.status}`;
  } catch {
    return raw || `HTTP ${response.status}`;
  }
}
